import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { refreshSession } from '@/lib/api';
import { useAuthStore } from '@/lib/auth-store';
import { useMe } from '@/api/queries';
import { Mark } from '@/components/AppShell';

/**
 * The auth gate.
 *
 * The access token lives in memory, so a reload starts with nothing. The
 * refresh cookie is what survives, and one `POST /auth/refresh` turns it back
 * into a session. Until that resolves we are neither signed in nor signed out,
 * and showing the sign-in page during that window would flash a login screen at
 * someone who is already signed in on every single reload.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const token = useAuthStore((s) => s.accessToken);
  const bootstrapped = useAuthStore((s) => s.bootstrapped);
  const markBootstrapped = useAuthStore((s) => s.markBootstrapped);
  const setUser = useAuthStore((s) => s.setUser);
  const location = useLocation();
  const [attempted, setAttempted] = useState(false);

  useEffect(() => {
    if (attempted || token) return;
    setAttempted(true);
    void (async () => {
      await refreshSession();
      markBootstrapped();
    })();
  }, [attempted, token, markBootstrapped]);

  // The role guard is server-side and authoritative; this call is how the shell
  // learns the display name, and how a revoked account is noticed promptly.
  const me = useMe(Boolean(token));
  useEffect(() => {
    if (me.data) setUser(me.data);
  }, [me.data, setUser]);

  if (!bootstrapped && !token) {
    return (
      <div className="chassis flex items-center justify-center">
        <span className="text-graphite-900">
          <Mark className="h-8 w-8 animate-ledger-pulse" />
        </span>
        <span className="sr-only">Checking your session</span>
      </div>
    );
  }

  if (!token) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
}
